// scroll-transition.js
// Utility classes for scroll tracking and view transitions
// No external deps. Provides ScrollIdleVT and ScrollTracker classes.

(() => {
  // -----------------------
  // Scroll-idle scheduler
  // -----------------------
  class ScrollIdleVT {
    constructor(startVT) {
      this.startVT = startVT;
      this.pending = null;
      this.idleDelay = 300;
      this.timer = 0;
      this.raf = 0;

      // Native scrollend if available, else debounce fallback
      this._onScroll = this._onScroll.bind(this);
      if (!("onscrollend" in window)) {
        window.addEventListener("scroll", this._onScroll, { passive: true });
      } else {
        window.addEventListener("scrollend", (e) => this._maybeRun(e));
      }
    }
    request(nextLayout, nextSelectId) {
      this.pending = nextLayout;
      this.nextSelectId = nextSelectId;
      if ("onscrollend" in window) return; // wait for native event
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this._maybeRun(), this.idleDelay);
    }
    _onScroll() {
      // debounce only
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this._maybeRun(), this.idleDelay);
    }
    _maybeRun() {
      if (this.pending == null) return;
      cancelAnimationFrame(this.raf);
      // double RAF to ensure final scroll layout is committed
      this.raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const next = this.pending;
          const nextSelectId = this.nextSelectId;
          this.pending = null;
          this.startVT(next, nextSelectId);
        });
      });
    }
    destroy() {
      if (!("onscrollend" in window)) {
        window.removeEventListener("scroll", this._onScroll);
      }
      clearTimeout(this.timer);
      cancelAnimationFrame(this.raf);
    }
  }

  // -----------------------
  // Simple ScrollTracker
  // -----------------------
  class ScrollTracker {
    constructor({ threshold = 0.3 } = {}) {
      this._trackedIds = [];
      this._callbacks = new Set();
      this._lastTriggered = null;
      this._active = false;
      this._isTicking = false;
      this.threshold = threshold;

      this._onScroll = this._onScroll.bind(this);
    }
    init() {
      if (this._active) return;
      this._active = true;
      window.addEventListener("scroll", this._onScroll, { passive: true });
      this._check();
    }
    destroy() {
      if (!this._active) return;
      this._active = false;
      window.removeEventListener("scroll", this._onScroll);
      this._trackedIds = [];
      this._callbacks.clear();
      this._lastTriggered = null;
    }
    add(ids, cb) {
      const list = Array.isArray(ids) ? ids : [ids];
      for (const id of list) {
        if (!this._trackedIds.includes(id)) this._trackedIds.push(id);
      }
      if (typeof cb === "function") this._callbacks.add(cb);
      this.init();
      this._check();
    }
    remove(ids) {
      const list = Array.isArray(ids) ? ids : [ids];
      this._trackedIds = this._trackedIds.filter((id) => !list.includes(id));
      if (this._trackedIds.length === 0) this.destroy();
    }
    _onScroll() {
      if (this._isTicking) return;
      this._isTicking = true;
      requestAnimationFrame(() => {
        this._check();
        this._isTicking = false;
      });
    }
    _check() {
      if (!this._trackedIds.length) return;
      const vh = window.innerHeight;
      const topBand = vh * this.threshold; // top portion of viewport
      for (const id of this._trackedIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const { top } = el.getBoundingClientRect();
        if (top >= 0 && top <= topBand) {
          if (this._lastTriggered !== id) {
            this._lastTriggered = id;
            for (const cb of this._callbacks) {
              try {
                cb(id, el);
              } catch (e) {
                console.error(e);
              }
            }
          }
          break;
        }
      }
    }
  }

  // -----------------------
  // View Transition Manager
  // -----------------------
  class ViewTransitionManager {
    constructor(layoutRoot, visibleByLayout = null, opts = {}) {
      this.layoutRoot = layoutRoot;
      this.opts = {
        namePrefix: "card",
        addProgressClass: true,
        useEnterExitHooks: true,
        ...opts,
      };
      this._running = false;
      this._pending = null;
      this._prepareCardRegistry(visibleByLayout);
    }

    _prepareCardRegistry(visibleByLayout) {
      const cards = Array.from(this.layoutRoot.querySelectorAll("[data-card]"));
      cards.forEach((el) => {
        const n = el.getAttribute("data-card");
        if (!el.style.viewTransitionName) {
          el.style.viewTransitionName = `${this.opts.namePrefix}-${n}-stable`;
        }
      });

      this._visibleByLayout = visibleByLayout || {
        0: new Set([1]),
        1: new Set([1, 2]),
        2: new Set([1, 2, 3]),
        3: new Set([1, 2, 3, 4]),
        4: new Set([1, 2, 3, 4, 5]),
      };
    }

    _computeEnterExit(prevLayout, nextLayout) {
      const prevSet = this._visibleByLayout?.[prevLayout] ?? new Set();
      const nextSet = this._visibleByLayout?.[nextLayout] ?? new Set();
      const entering = [...nextSet].filter((n) => !prevSet.has(n));
      const exiting = [...prevSet].filter((n) => !nextSet.has(n));
      return { entering, exiting };
    }

    _getCard = (n) => this.layoutRoot.querySelector(`[data-card="${n}"]`);

    /*
     * Move the layout root to `nextLayout`, running `updateContent` inside a
     * view transition when one can play. Calls are serialized: a request that
     * arrives while a transition runs replaces any earlier waiting request and
     * plays once the current one finishes, so the last selection always wins
     * and nothing is dropped.
     */
    async setLayout(nextLayout, updateContent, afterUpdate) {
      if (!this.layoutRoot) return;
      if (this._running) {
        this._pending = { nextLayout, updateContent, afterUpdate };
        return;
      }
      this._running = true;
      this.layoutRoot.classList.add("vt-running");
      try {
        await this._transition(nextLayout, updateContent, afterUpdate);
      } finally {
        this._running = false;
        this.layoutRoot?.classList.remove("vt-running");
        const pending = this._pending;
        this._pending = null;
        if (pending && this.layoutRoot) {
          this.setLayout(
            pending.nextLayout,
            pending.updateContent,
            pending.afterUpdate
          );
        }
      }
    }

    // A view transition snapshots the page; only worth it when the gallery is
    // actually on screen and the tab is visible. (The OS reduced-motion
    // preference is deliberately not consulted here: the theme has never
    // honoured it for this gallery, and gating on it made the layout flash for
    // anyone with the setting on.)
    _canAnimate() {
      return (
        typeof document.startViewTransition === "function" &&
        document.visibilityState === "visible" &&
        this.layoutRoot.getClientRects().length > 0
      );
    }

    _markCards(entering, exiting, active) {
      if (!this.opts.useEnterExitHooks) return;
      const mark = (n, kind) => {
        const cls = `vt-${kind}-card-${n}`;
        this.layoutRoot.classList.toggle(cls, active);
        const card = this._getCard(n);
        if (card) {
          card.style.viewTransitionName = active
            ? `${this.opts.namePrefix}-${n}`
            : `${this.opts.namePrefix}-${n}-stable`;
        }
      };
      entering.forEach((n) => mark(n, "enter"));
      exiting.forEach((n) => mark(n, "exit"));
      this.layoutRoot.classList.toggle("will-change-transform", active);
    }

    async _transition(nextLayout, updateContent, afterUpdate) {
      const prevStr = this.layoutRoot.getAttribute("layout");
      const prev = prevStr != null ? Number(prevStr) : 0;
      const nextNum = Number(nextLayout);
      const { entering, exiting } = this._computeEnterExit(prev, nextNum);

      const applyChange = () => {
        if (typeof updateContent === "function") updateContent();
        this.layoutRoot.setAttribute("layout", String(nextNum));
      };

      if (!this._canAnimate()) {
        applyChange();
        if (typeof afterUpdate === "function") afterUpdate();
        return;
      }

      this._markCards(entering, exiting, true);
      if (this.opts.addProgressClass) {
        document.documentElement.classList.add("view-transition-in-progress");
      }

      const vt = document.startViewTransition(applyChange);
      // A transition that fights the visitor's scroll looks worse than none:
      // skip it as soon as they move.
      const cancel = () => vt.skipTransition();
      const listen = { passive: true };
      addEventListener("scroll", cancel, listen);
      addEventListener("wheel", cancel, listen);
      addEventListener("touchmove", cancel, listen);
      try {
        await vt.finished;
      } catch (e) {
        // Skipped (scroll) or aborted (tab hidden) transitions still applied
        // the DOM change; only a failure inside updateContent is worth noise.
        const benign =
          e && (e.name === "AbortError" || e.name === "InvalidStateError");
        if (!benign) console.error(e);
      } finally {
        removeEventListener("scroll", cancel, listen);
        removeEventListener("wheel", cancel, listen);
        removeEventListener("touchmove", cancel, listen);
        if (typeof afterUpdate === "function") afterUpdate();
        if (this.layoutRoot) this._markCards(entering, exiting, false);
        if (this.opts.addProgressClass) {
          document.documentElement.classList.remove(
            "view-transition-in-progress"
          );
        }
      }
    }
  }

  // Add destroy to ViewTransitionManager for cleanup on disconnect
  ViewTransitionManager.prototype.destroy = function () {
    this.layoutRoot = null;
    this._visibleByLayout = null;
    this._pending = null;
  };

  // Expose utility classes
  window.ScrollTransition = {
    ScrollIdleVT,
    ScrollTracker,
    ViewTransitionManager,
  };
})();
